import { openDB } from 'idb';
import { DATA_VERSION } from './version';

export class DataManager {
  constructor(accountId) {
    this.modelsToSync = ['inbox', 'label', 'team'];
    this.accountId = accountId;
    this.db = null;
  }

  async initDb() {
    if (this.db) return this.db;
    const dbName = `cw-store-${this.accountId}`;

    // IndexedDB can occasionally be very slow to open (browser contention, quota checks).
    // Time out after 5s so the caller can fall back to network.
    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('IndexedDB open timed out')), 5000)
    );

    this.db = await Promise.race([
      openDB(`cw-store-${this.accountId}`, DATA_VERSION, {
        upgrade(db) {
          db.createObjectStore('cache-keys');
          db.createObjectStore('inbox', { keyPath: 'id' });
          db.createObjectStore('label', { keyPath: 'id' });
          db.createObjectStore('team', { keyPath: 'id' });
        },
      }),
      timeout,
    ]);

    // Store the database name in LocalStorage
    try {
      const dbNames = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
      if (!dbNames.includes(dbName)) {
        dbNames.push(dbName);
        localStorage.setItem('cw-idb-names', JSON.stringify(dbNames));
      }
    } catch {
      // localStorage may be full or unavailable
    }

    return this.db;
  }

  validateModel(name) {
    if (!name) throw new Error('Model name is not defined');
    if (!this.modelsToSync.includes(name)) {
      throw new Error(`Model ${name} is not defined`);
    }
    return true;
  }

  async replace({ modelName, data }) {
    this.validateModel(modelName);

    await this.db.clear(modelName);
    return this.push({ modelName, data });
  }

  async push({ modelName, data }) {
    this.validateModel(modelName);

    if (Array.isArray(data)) {
      const tx = this.db.transaction(modelName, 'readwrite');
      data.forEach(item => {
        tx.store.add(item);
      });
      await tx.done;
    } else {
      await this.db.add(modelName, data);
    }
  }

  async get({ modelName }) {
    this.validateModel(modelName);
    return this.db.getAll(modelName);
  }

  async setCacheKeys(cacheKeys) {
    const keys = Object.keys(cacheKeys);
    for (let i = 0; i < keys.length; i++) {
      const modelName = keys[i];
      await this.db.put('cache-keys', cacheKeys[modelName], modelName);
    }
  }

  async getCacheKey(modelName) {
    this.validateModel(modelName);

    return this.db.get('cache-keys', modelName);
  }
}
